"use client";

import React, { useRef } from "react";
import Image from "next/image";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { PortableText } from "@portabletext/react";

import { customRichTextComponent } from "@/sanity/sanityPortableText";
import {
  IManifestoParagraph,
  PageManifestoSchema,
} from "@/sanity/pages/PageManifestoTemplate";
import Header from "@/components/global/partials/Header";
import FooterSection from "@/components/global/sections/home/FooterSection";
import { FadeIn, FADE_EASE } from "@/components/global/utils/FadeIn";

import signatureBlue from "@/public/manifesto/signature-andrea.svg";
import signatureDark from "@/public/manifesto/signature-andrea-dark.svg";
import missionIconFallback from "@/public/manifesto/mission-icon.svg";
import stampFallback from "@/public/manifesto/stamp.png";
import bg from "@/public/manifesto/bg.jpg";
import bgM from "@/public/manifesto/bg-m.png";

// Decorative fading pipes flanking the hero label — ||| LABEL |||
function LabelPipes() {
  return (
    <>
      <span className="text-[#B8D2E2]/20">|</span>
      <span className="text-[#B8D2E2]/40">|</span>
      <span className="text-[#B8D2E2]/60">|</span>
    </>
  );
}

function Divider() {
  return <hr className="w-full border-t border-black/10" />;
}

// Sticky white veil — the text below the reading line fades into white and
// "unfades" as it scrolls up from under the gradient (Figma node 2416:540).
// Dissolves over the last stretch of the letter so the sign-off ends fully inked.
function UnfadeVeil({ target }: { target: React.RefObject<HTMLElement> }) {
  const { scrollYProgress } = useScroll({
    target,
    offset: ["start start", "end end"],
  });
  const opacity = useTransform(scrollYProgress, [0.75, 0.95], [1, 0]);

  return (
    <div
      aria-hidden
      className="absolute inset-y-0 left-1/2 z-10 -translate-x-1/2 pointer-events-none w-[min(100rem,100vw)]"
    >
      <motion.div
        style={{ opacity }}
        className="sticky top-0 h-screen w-full bg-gradient-to-b from-white/0 from-[43.99%] to-white to-[94.71%]"
      />
    </div>
  );
}

// Dotted scroll track — the blue arrow tracks reading progress through `target`
function ManifestoScrollbar({
  target,
}: {
  target: React.RefObject<HTMLElement>;
}) {
  const { scrollYProgress } = useScroll({
    target,
    offset: ["start start", "end end"],
  });
  const progress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.4,
  });
  // Track is 28.9375rem tall, the arrow 1.0825rem — travel the difference
  const y = useTransform(progress, [0, 1], ["0rem", "27.855rem"]);

  return (
    <div className="relative h-[28.9375rem] w-[1.75rem]">
      <svg
        aria-hidden
        preserveAspectRatio="none"
        viewBox="0 0 14 231.5"
        fill="none"
        className="absolute inset-0 w-full h-full"
      >
        <path
          d="M7 0V231.5"
          stroke="black"
          strokeOpacity="0.24"
          strokeWidth="14"
          strokeDasharray="1 13"
        />
      </svg>

      <motion.svg
        aria-hidden
        style={{ y }}
        viewBox="0 0 32.33 8.66"
        fill="none"
        className="absolute top-0 left-0 max-w-none h-[1.0825rem] w-[4.04125rem]"
      >
        <rect y="3.58" width="28" height="1.5" fill="#3A8BBD" />
        <path d="M32.33 4.33 28 0 23.67 4.33 28 8.66Z" fill="#3A8BBD" />
      </motion.svg>
    </div>
  );
}

// Signature wipe — writes in left-to-right, scrubbed by the sign-off row
// entering the viewport. Scroll-driven (no IntersectionObserver) so the
// signature can never get stuck hidden; at worst it renders fully inked.
function SignatureReveal({
  target,
  children,
}: {
  target: React.RefObject<HTMLElement>;
  children: React.ReactNode;
}) {
  const { scrollYProgress } = useScroll({
    target,
    offset: ["start end", "start 0.6"],
  });
  const progress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 25,
    mass: 0.5,
  });
  const clipPath = useTransform(
    progress,
    [0, 1],
    ["inset(0 100% 0 0)", "inset(0 0% 0 0)"],
  );

  return <motion.div style={{ clipPath }}>{children}</motion.div>;
}

function Paragraphs({ blocks }: { blocks?: IManifestoParagraph[] }) {
  if (!blocks?.length) return null;

  return (
    <>
      {blocks.map((block) => (
        <div
          key={block._key}
          className="w-full text-black opacity-60 font-geist text-[2.25rem] leading-[1.5] lg:text-[2.5rem]"
        >
          <PortableText
            value={block.text ?? []}
            components={customRichTextComponent({
              block: {
                normal: ({ children }) => (
                  <p className="leading-[1.5]">{children}</p>
                ),
              },
            })}
          />
        </div>
      ))}
    </>
  );
}

export default function PageManifestoJSX({
  data,
}: {
  data: PageManifestoSchema;
}) {
  const {
    heroLabel,
    heroTitle,
    heroSignature,
    letterTitle,
    paragraphsIntro,
    missionIcon,
    missionTitle,
    missionText,
    paragraphsMiddle,
    cardTitle,
    cardText,
    paragraphsClosing,
    closingLine,
    signatureImage,
    signatureName,
    signatureRole,
    stampImage,
    footerLogo,
    footerHeadline,
    footerSubtext,
    footerCTA,
    footerColumns,
    footerCopyright,
    footerLegalLinks,
  } = data;

  const letterRef = useRef<HTMLElement>(null);
  const signoffRef = useRef<HTMLDivElement>(null);

  // Hero parallax — scrubbed by the letter section sliding over the pinned
  // hero: the copy drifts up slightly slower than the scroll while the bg
  // zooms in, adding depth to the reveal.
  const { scrollYProgress: heroProgress } = useScroll({
    target: letterRef,
    offset: ["start end", "start start"],
  });
  const heroContentY = useTransform(heroProgress, [0, 1], ["0rem", "-6rem"]);
  const heroBgScale = useTransform(heroProgress, [0, 1], [1, 1.1]);

  return (
    <main className="relative bg-black">
      {/* ── Hero — pinned while the letter scrolls over it ────────────────── */}
      <section className="sticky top-0 w-full bg-black lg:h-screen overflow-clip px-[2rem] h-[94.375rem]">
        <motion.div
          style={{ scale: heroBgScale }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: FADE_EASE }}
          className="absolute inset-0 w-full h-full origin-center z-[1]"
        >
          <Image
            src={bg}
            alt=""
            quality={100}
            className="hidden object-cover h-full lg:block"
          />
          <Image
            src={bgM}
            alt=""
            quality={100}
            className="object-cover h-full lg:hidden"
          />
        </motion.div>
        {data?.settings?.header && (
          <Header
            data={data.settings.header}
            headerStyle="dark"
            className="relative z-[6] lg:top-[1rem]"
            showBorder
          />
        )}

        <motion.div
          style={{ y: heroContentY }}
          className="mx-auto flex lg:w-[min(61.125rem,100%)] w-[min(42.75rem,100%)] flex-col items-center gap-[2rem] pt-[8rem] lg:pt-[18rem] relative z-[5] "
        >
          <div className="flex flex-col items-center w-full gap-[1.5rem]">
            {heroLabel && (
              <FadeIn delay={0} className="w-full">
                <p className="m-0 w-full leading-normal text-center uppercase font-jetbrainsmono text-[1.75rem] text-[#B8D2E2]">
                  <LabelPipes /> {heroLabel} <LabelPipes />
                </p>
              </FadeIn>
            )}

            {heroTitle && (
              <FadeIn delay={0.1} className="w-full">
                <h1 className="m-0 w-full font-normal text-center text-white font-news text-[5.5rem] leading-[1.1] tracking-[-0.15rem] lg:text-[7.5rem]">
                  <PortableText
                    value={heroTitle}
                    components={customRichTextComponent({
                      block: { normal: ({ children }) => <>{children}</> },
                      marks: {
                        em: ({ children }) => (
                          <em className="italic font-news">{children}</em>
                        ),
                      },
                    })}
                  />
                </h1>
              </FadeIn>
            )}
          </div>
          <FadeIn delay={0} className="">
            <Image
              src={heroSignature?.src ?? signatureBlue}
              alt={signatureName ?? "Andrea Cimitan"}
              width={heroSignature?.width ?? 273}
              height={heroSignature?.height ?? 54}
              className="w-auto h-[5.5rem] lg:h-[6.75rem]"
            />
          </FadeIn>
        </motion.div>
      </section>

      {/* ── Letter ────────────────────────────────────────────────────────── */}
      <section
        ref={letterRef}
        className="flex relative z-10 justify-center w-full bg-white lg:px-0 px-[3rem] py-[8rem] lg:py-[13.75rem]"
      >
        <div className="relative flex w-[min(83.5rem,100%)] flex-col items-center gap-[4rem]">
          {/* Decorative dotted scroll bar — desktop only */}
          <UnfadeVeil target={letterRef} />

          <div className="hidden absolute inset-y-0 z-20 pointer-events-none xl:block left-[-33.5rem] w-[3.75rem]">
            <div className="sticky top-0 w-full h-[45.75rem]">
              <div className="absolute left-px top-[16.875rem]">
                <ManifestoScrollbar target={letterRef} />
              </div>
            </div>
          </div>

          <div className="flex flex-col w-full gap-[4rem]">
            {letterTitle && (
              <h2 className="font-normal leading-none text-black max-w-[79.5rem] font-news text-[5rem] tracking-[-0.25rem] lg:text-[6rem] [&_*]:m-0 ">
                <PortableText
                  value={letterTitle}
                  components={customRichTextComponent({
                    block: { normal: ({ children }) => <>{children}</> },
                    marks: {
                      em: ({ children }) => (
                        <em className="italic font-news text-black/50">
                          {children}
                        </em>
                      ),
                    },
                  })}
                />
              </h2>
            )}

            <div className="flex flex-col items-start w-full  gap-[3.5rem] [&_*]:m-0">
              <Divider />

              <Paragraphs blocks={paragraphsIntro} />

              <Divider />

              {/* Mission block */}
              {(missionTitle || missionText) && (
                <div className="flex flex-col items-start w-full gap-[1.5rem]">
                  <Image
                    src={missionIcon?.src ?? missionIconFallback}
                    alt=""
                    width={missionIcon?.width ?? 43}
                    height={missionIcon?.height ?? 44}
                    className="h-[5.5rem] w-[5.375rem]"
                  />

                  <div className="flex flex-col w-full gap-[0.75rem]">
                    {missionTitle && (
                      <h3 className="w-full font-normal text-black font-news text-[3rem] leading-[1.5] tracking-[-0.0625rem]">
                        <PortableText
                          value={missionTitle}
                          components={customRichTextComponent({
                            block: {
                              normal: ({ children }) => <>{children}</>,
                            },
                            marks: {
                              em: ({ children }) => (
                                <em className="italic underline font-news decoration-from-font [text-underline-position:from-font]">
                                  {children}
                                </em>
                              ),
                            },
                          })}
                        />
                      </h3>
                    )}

                    {missionText && (
                      <p className="w-full text-black opacity-60 font-geist text-[2.25rem] leading-[1.5] lg:text-[2.5rem]">
                        {missionText}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <Divider />

              <Paragraphs blocks={paragraphsMiddle} />

              {/* Highlight card */}
              {(cardTitle || cardText) && (
                <div className="flex relative flex-col w-full gap-[0.75rem] rounded-[0.75rem] bg-black/[0.02] px-[3.5rem] py-[2.75rem] shadow-[inset_0_0.5rem_3.3375rem_0_rgba(0,0,0,0.04)]">
                  {cardTitle && (
                    <h3 className="w-full font-normal text-black underline font-news text-[3rem] leading-[1.5] tracking-[-0.0625rem] decoration-from-font [text-underline-position:from-font]">
                      {cardTitle}
                    </h3>
                  )}

                  {cardText && (
                    <p className="w-full text-black opacity-60 font-geist text-[2.25rem] leading-[1.5] lg:text-[2.5rem]">
                      {cardText}
                    </p>
                  )}
                </div>
              )}

              <Paragraphs blocks={paragraphsClosing} />

              {closingLine && (
                <p className="m-0 w-full font-normal text-black font-news text-[3rem] leading-[1.5] tracking-[-0.0625rem]">
                  <PortableText
                    value={closingLine}
                    components={customRichTextComponent({
                      block: { normal: ({ children }) => <>{children}</> },
                      marks: {
                        em: ({ children }) => (
                          <em className="italic font-news">{children}</em>
                        ),
                      },
                    })}
                  />
                </p>
              )}

              <Divider />
            </div>
          </div>

          {/* Sign-off row */}
          <div
            ref={signoffRef}
            className="flex justify-between items-center w-full"
          >
            <div className="flex flex-col items-start w-[34rem]">
              <SignatureReveal target={signoffRef}>
                <Image
                  src={signatureImage?.src ?? signatureDark}
                  alt={signatureName ?? "Andrea Cimitan"}
                  width={signatureImage?.width ?? 273}
                  height={signatureImage?.height ?? 54}
                  className="w-auto h-[5.5rem] lg:h-[6.75rem]"
                />
              </SignatureReveal>

              {(signatureName || signatureRole) && (
                <div className="w-full text-black opacity-40 font-geist text-[2rem] leading-[1.5] lg:text-[2.25rem] prose-p:m-0">
                  {signatureName && (
                    <p className="uppercase">{signatureName}</p>
                  )}
                  {signatureRole && <p className="italic">{signatureRole}</p>}
                </div>
              )}
            </div>

            {/* Wax stamp — full spin then hold, on repeat (Figma motion) */}
            <motion.div className="flex justify-center items-center h-[16.8rem] w-[16rem]">
              <div className="overflow-hidden relative h-[14.66rem] w-[13.625rem]">
                <Image
                  src={stampImage?.src ?? stampFallback}
                  alt=""
                  width={stampImage?.width ?? 1044}
                  height={stampImage?.height ?? 1507}
                  placeholder={stampImage?.lqip ? "blur" : undefined}
                  blurDataURL={stampImage?.lqip}
                  className="absolute left-0 top-[-16.56%] h-[134.19%] w-full max-w-none object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Footer — same as homepage ─────────────────────────────────────── */}
      <div className="relative z-10">
        <FooterSection
          logo={footerLogo}
          headline={footerHeadline}
          subtext={footerSubtext}
          cta={footerCTA}
          columns={footerColumns}
          copyright={footerCopyright}
          legalLinks={footerLegalLinks}
        />
      </div>
    </main>
  );
}
